import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
} from "react";
import { useAuth } from "../../contexts/AuthContext";
import "./Admin.css";
import { useChangesTracker } from "./useChangesTracker";
import ChangesPreviewModal from "./ChangesPreviewModal";
import ChangesPopover from "./ChangesPopover";
import ChangePasswordModal from "./ChangePasswordModal";
import PessoasEditor from "./PessoasEditor";
import PesquisaEditor from "./PesquisaEditor";
import EventosEditor from "./EventosEditor";
import LocalesEditor from "./LocalesEditor";
import { saveSection, isTokenExpired } from "../../services/api";
import { writePreview } from "../../services/preview";

type TabType = "pessoas" | "pesquisa" | "eventos" | "locales";

// Create context for changes tracker
const ChangesContext = createContext<ReturnType<
  typeof useChangesTracker
> | null>(null);

export const useChanges = () => {
  const context = useContext(ChangesContext);
  if (!context)
    throw new Error("useChanges must be used within AdminDashboard");
  return context;
};

// Context for editor data getters. Each editor exposes the API section(s) it
// owns and the current data object for each, ready to PUT to the worker.
export interface EditorSectionData {
  section: string;
  data: unknown;
}

interface EditorDataContextType {
  registerEditor: (label: string, getData: () => EditorSectionData[]) => void;
  unregisterEditor: (label: string) => void;
}

const EditorDataContext = createContext<EditorDataContextType | null>(null);

export const useEditorData = () => {
  const context = useContext(EditorDataContext);
  if (!context)
    throw new Error("useEditorData must be used within AdminDashboard");
  return context;
};

const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>("pessoas");
  const [showPreview, setShowPreview] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const changesTracker = useChangesTracker();
  const editorDataGettersRef = useRef<
    Map<string, () => EditorSectionData[]>
  >(new Map());

  // Warn before a full page unload (reload / tab close) while edits are pending.
  const hasChanges = changesTracker.hasChanges;
  // Set right before an intentional discard+reload so the browser's native
  // unload prompt doesn't stack on top of our own confirm dialog.
  const bypassUnloadGuardRef = useRef(false);
  useEffect(() => {
    if (!hasChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      if (bypassUnloadGuardRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasChanges]);

  const collectSections = (): EditorSectionData[] =>
    Array.from(editorDataGettersRef.current.values()).flatMap((getData) =>
      getData()
    );

  // Stash the current (unsaved) editor state and open the public site in a new
  // tab rendering it, so the admin can see exactly how it'll look before saving.
  const handlePreviewSite = () => {
    writePreview(collectSections());
    window.open("/?preview=1", "_blank", "noopener");
  };

  const handleLogout = () => {
    if (
      hasChanges &&
      !window.confirm("Você tem alterações não salvas. Sair mesmo assim?")
    ) {
      return;
    }
    logout();
  };

  const registerEditor = (
    label: string,
    getData: () => EditorSectionData[]
  ) => {
    editorDataGettersRef.current.set(label, getData);
  };

  const unregisterEditor = (label: string) => {
    editorDataGettersRef.current.delete(label);
  };

  const handleSaveAllClick = () => {
    if (changesTracker.hasChanges) {
      console.log(
        "Opening preview modal with changes:",
        changesTracker.changes
      );
      setShowPreview(true);
    } else {
      console.log("No changes to save");
    }
  };

  const handleConfirmSave = async () => {
    if (!user) {
      setSaveError("Usuário não autenticado");
      return;
    }

    setShowPreview(false);

    // Drop an expired session up front instead of letting each PUT 401 mid-batch.
    if (isTokenExpired(user.token)) {
      setSaveError("Sessão expirada. Faça login novamente.");
      logout();
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // Each changed editor (keyed by its section label) hands us the API
      // section(s) it owns plus the current data to persist. We save per label
      // so a single failure doesn't hide the sections that did persist.
      const changedLabels = Array.from(
        new Set(changesTracker.changes.map((c) => c.section))
      );

      const labelSaves = changedLabels
        .map((label) => {
          const getData = editorDataGettersRef.current.get(label);
          if (!getData) return null;
          const promise = Promise.all(
            getData().map(({ section, data }) =>
              saveSection(user.token, section, data)
            )
          );
          return { label, promise };
        })
        .filter((x): x is { label: string; promise: Promise<void[]> } => !!x);

      if (labelSaves.length === 0) {
        changesTracker.markSaved();
        return;
      }

      const results = await Promise.allSettled(labelSaves.map((l) => l.promise));
      const failed = labelSaves
        .filter((_, i) => results[i].status === "rejected")
        .map((l) => l.label);

      if (failed.length === 0) {
        // Rebaseline editors in place (markSaved) — no reload, so we never read
        // back stale, not-yet-propagated content from KV.
        changesTracker.markSaved();
      } else {
        const firstError = results.find(
          (r) => r.status === "rejected"
        ) as PromiseRejectedResult | undefined;
        const detail =
          firstError && firstError.reason instanceof Error
            ? ` (${firstError.reason.message})`
            : "";
        setSaveError(
          `Falha ao salvar: ${failed.join(", ")}.${detail} Tente novamente.`
        );
      }
    } catch (err) {
      console.error("Error saving changes:", err);
      setSaveError(
        err instanceof Error ? err.message : "Falha ao salvar alterações"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelChanges = () => {
    if (
      window.confirm(
        "Tem certeza que deseja descartar todas as alterações pendentes? Esta ação não pode ser desfeita."
      )
    ) {
      // Clear all changes from tracker
      changesTracker.clearAllChanges();
      // We're intentionally discarding — skip the native unload prompt that
      // would otherwise stack on top of the confirm above.
      bypassUnloadGuardRef.current = true;
      // Reload the page to reset all editors to original state
      window.location.reload();
    }
  };

  const handleTabChange = (newTab: TabType) => {
    if (isEditing) {
      if (
        window.confirm(
          "Você está editando um item. Deseja descartar as alterações e mudar de aba?"
        )
      ) {
        setIsEditing(false);
        setActiveTab(newTab);
      }
    } else {
      setActiveTab(newTab);
    }
  };

  const tabs = [
    { id: "pessoas", label: "Pessoas" },
    { id: "pesquisa", label: "Pesquisa" },
    { id: "eventos", label: "Eventos" },
    { id: "locales", label: "Traduções" },
  ] as const;

  return (
    <ChangesContext.Provider value={changesTracker}>
      <EditorDataContext.Provider value={{ registerEditor, unregisterEditor }}>
        <div className="admin-dashboard">
          <div className="admin-header">
            <h1>Painel Administrativo</h1>
            <div className="user-info">
              <span>Conectado como: {user?.username}</span>
              {changesTracker.hasChanges && (
                <ChangesPopover changes={changesTracker.changes} />
              )}
              {changesTracker.hasChanges && (
                <>
                  <button
                    onClick={handlePreviewSite}
                    className="preview-site-button"
                    disabled={isSaving}
                    title="Abrir o site com as alterações ainda não salvas"
                  >
                    Pré-visualizar site
                  </button>
                  <button
                    onClick={handleCancelChanges}
                    className="cancel-all-button"
                    disabled={isSaving}
                  >
                    Cancelar Alterações
                  </button>
                  <button
                    onClick={handleSaveAllClick}
                    className="save-all-button"
                    disabled={isSaving}
                  >
                    {isSaving ? "Salvando..." : "Salvar Todas as Alterações"}
                  </button>
                </>
              )}
              <button
                onClick={() => setShowChangePassword(true)}
                className="change-password-button"
              >
                Alterar senha
              </button>
              <button onClick={handleLogout} className="logout-button">
                Sair
              </button>
            </div>
          </div>

          {saveError && (
            <div className="error" style={{ margin: "10px", padding: "10px" }}>
              {saveError}
            </div>
          )}

          <div className="admin-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="admin-content">
            <div
              style={{ display: activeTab === "pessoas" ? "block" : "none" }}
            >
              <PessoasEditor onEditingChange={setIsEditing} />
            </div>
            <div
              style={{ display: activeTab === "pesquisa" ? "block" : "none" }}
            >
              <PesquisaEditor onEditingChange={setIsEditing} />
            </div>
            <div
              style={{ display: activeTab === "eventos" ? "block" : "none" }}
            >
              <EventosEditor onEditingChange={setIsEditing} />
            </div>
            <div
              style={{ display: activeTab === "locales" ? "block" : "none" }}
            >
              <LocalesEditor />
            </div>
          </div>

          <ChangesPreviewModal
            isOpen={showPreview}
            changes={changesTracker.changes}
            onConfirm={handleConfirmSave}
            onCancel={() => {
              console.log("Closing preview modal");
              setShowPreview(false);
            }}
          />

          {showChangePassword && (
            <ChangePasswordModal
              onClose={() => setShowChangePassword(false)}
            />
          )}
        </div>
      </EditorDataContext.Provider>
    </ChangesContext.Provider>
  );
};

export default AdminDashboard;
